// src/redux/slices/adminSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ADMIN_TOKEN_KEY, ADMIN_USER_KEY, ADMIN_GUARD_KEY } from '../constants/authKeys';
import { adminNotificationsApi } from '../../config/adminApi';
import { API_BASE_URL } from '@/config/api';

// Async thunk handles login + persistence
export const adminLogin = createAsyncThunk(
  '/gateway',
  async (credentials, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_BASE_URL}/adminLogin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(credentials),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const backendMessage =
          data?.error || data?.message || (data?.errors && Object.values(data.errors).flat().join(' ')) ||
          'Authentication failed';
        return rejectWithValue(backendMessage);
      }

      // normalize payload
      return {
        token: data.access_token,
        user: data.user || data.admin || {},
        adminuser: data.adminuser || data.user || data.admin || {},
        guard: data.guard || null,
        tokenType: data.token_type || 'bearer',
        menus: Array.isArray(data.menus) ? data.menus : [] // Ensure menus is array
      };
    } catch (err) {
      return rejectWithValue('Network error');
    }
  }
);

// Async thunk for fetching notifications
export const fetchNotifications = createAsyncThunk(
  'admin/fetchNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminNotificationsApi.getNotifications();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch notifications');
    }
  }
);

// Async thunk for marking notification as read
export const markNotificationAsRead = createAsyncThunk(
  'admin/markNotificationAsRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await adminNotificationsApi.markAsRead(notificationId);
      return { notificationId, response };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to mark notification as read');
    }
  }
);

// Async thunk for marking all notifications as read
export const markAllNotificationsAsRead = createAsyncThunk(
  'admin/markAllNotificationsAsRead',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await adminNotificationsApi.markAllAsRead();
      dispatch(fetchNotifications());
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to mark all notifications as read');
    }
  }
);

// Async thunk for marking notifications by type as read
export const markNotificationsReadByType = createAsyncThunk(
  'admin/markNotificationsReadByType',
  async (type, { dispatch, rejectWithValue }) => {
    try {
      const response = await adminNotificationsApi.markByTypeAsRead(type);
      dispatch(fetchNotifications());
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to mark notifications by type as read');
    }
  }
);

const loadAdminState = () => {
  if (typeof window === 'undefined') return {
    user: null,
    adminuser: null,
    token: null,
    guard: null,
    menus: [],
    isLoading: false,
    error: null,
    isAuthenticated: false,
    notifications: {},
    unreadCount: 0
  };

  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  const user = localStorage.getItem(ADMIN_USER_KEY);
  const adminuser = localStorage.getItem('adminUserData'); // Store adminuser separately
  const guard = localStorage.getItem(ADMIN_GUARD_KEY);
  const menus = localStorage.getItem('adminMenus');

  if (token && user) {
    try {
      const parsedUser = JSON.parse(user);
      const parsedAdminUser = adminuser ? JSON.parse(adminuser) : parsedUser;
      let parsedMenus = [];
      try {
        parsedMenus = menus ? JSON.parse(menus) : [];
        if (!Array.isArray(parsedMenus)) parsedMenus = [];
      } catch (e) {
        parsedMenus = [];
      }

      return {
        user: parsedUser,
        adminuser: parsedAdminUser,
        token,
        guard,
        menus: parsedMenus,
        isLoading: false,
        error: null,
        isAuthenticated: true,
        notifications: {},
        unreadCount: 0
      };
    } catch {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(ADMIN_USER_KEY);
      localStorage.removeItem('adminUserData');
      localStorage.removeItem(ADMIN_GUARD_KEY);
      localStorage.removeItem('adminMenus');
    }
  }
  return {
    user: null,
    adminuser: null,
    token: null,
    guard: null,
    menus: [],
    isLoading: false,
    error: null,
    isAuthenticated: false,
    notifications: {},
    unreadCount: 0
  };
};

const adminSlice = createSlice({
  name: 'admin',
  initialState: loadAdminState(),
  reducers: {
    logoutAdmin: (state) => {
      state.user = null;
      state.adminuser = null;
      state.token = null;
      state.guard = null;
      state.menus = [];
      state.isAuthenticated = false;
      state.error = null;
      state.isLoading = false;
      state.notifications = {};
      state.unreadCount = 0;
      if (typeof window !== 'undefined') {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(ADMIN_USER_KEY);
        localStorage.removeItem('adminUserData');
        localStorage.removeItem(ADMIN_GUARD_KEY);
        localStorage.removeItem('adminMenus');
      }
    },
    clearAdminError: (state) => { state.error = null; }
  },
  extraReducers: (builder) => {
    builder
      .addCase(adminLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.adminuser = action.payload.adminuser;
        state.token = action.payload.token;
        state.guard = action.payload.guard;
        state.menus = Array.isArray(action.payload.menus) ? action.payload.menus : [];
        state.isAuthenticated = true;
        // persist
        if (typeof window !== 'undefined') {
          localStorage.setItem(ADMIN_TOKEN_KEY, action.payload.token || '');
          localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(action.payload.user || {}));
          localStorage.setItem('adminUserData', JSON.stringify(action.payload.adminuser || {}));
          localStorage.setItem('adminMenus', JSON.stringify(state.menus));
          if (action.payload.guard) localStorage.setItem(ADMIN_GUARD_KEY, action.payload.guard);
        }
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Login failed';
        state.isAuthenticated = false;
      })
      // Notification reducers
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        // Store the full notifications object
        state.notifications = action.payload.notifications || {};

        // Calculate unread count across all categories
        let count = 0;
        if (state.notifications && typeof state.notifications === 'object') {
          Object.values(state.notifications).forEach(categoryNotifications => {
            if (Array.isArray(categoryNotifications)) {
              count += categoryNotifications.filter(n => !n.is_read).length;
            }
          });
        }
        state.unreadCount = count;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const { notificationId } = action.payload;

        // Find and update the notification in the correct category
        if (state.notifications && typeof state.notifications === 'object') {
          Object.keys(state.notifications).forEach(category => {
            if (Array.isArray(state.notifications[category])) {
              const notificationIndex = state.notifications[category].findIndex(n => n.id === notificationId);
              if (notificationIndex !== -1) {
                state.notifications[category][notificationIndex].is_read = true;
              }
            }
          });

          // Recalculate unread count
          let count = 0;
          Object.values(state.notifications).forEach(categoryNotifications => {
            if (Array.isArray(categoryNotifications)) {
              count += categoryNotifications.filter(n => !n.is_read).length;
            }
          });
          state.unreadCount = count;
        }
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        // Mark all notifications as read across all categories
        if (state.notifications && typeof state.notifications === 'object') {
          Object.values(state.notifications).forEach(categoryNotifications => {
            if (Array.isArray(categoryNotifications)) {
              categoryNotifications.forEach(notification => {
                notification.is_read = true;
              });
            }
          });
        }
        // Reset unread count
        state.unreadCount = 0;
      })
      .addCase(markNotificationsReadByType.fulfilled, (state, action) => {
        const type = action.meta.arg; // The type argument passed to the thunk

        // Mark notifications of this type as read
        if (state.notifications && state.notifications[type] && Array.isArray(state.notifications[type])) {
          state.notifications[type].forEach(notification => {
            notification.is_read = true;
          });

          // Recalculate unread count
          let count = 0;
          Object.values(state.notifications).forEach(categoryNotifications => {
            if (Array.isArray(categoryNotifications)) {
              count += categoryNotifications.filter(n => !n.is_read).length;
            }
          });
          state.unreadCount = count;
        }
      });
  }
});

export const { logoutAdmin, clearAdminError } = adminSlice.actions;
export default adminSlice.reducer;
